import { createContext, useContext, useEffect, useState, useRef, useCallback, useMemo } from 'react'
import { supabase } from '@/supabase/client'
import { User } from '@supabase/supabase-js'
import { useLocation, useNavigate } from 'react-router-dom'
import { v4 as uuidv4 } from 'uuid'
import { logError } from '@/lib/sentry'

// 인증 옵션 타입
interface AuthOptions {
  redirectTo?: string
}

// 인증 컨텍스트 타입
interface AuthContextType {
  user: User | null
  pseudoUserId: string
  signInWithKakao: (options?: AuthOptions) => Promise<void>
  signInWithGoogle: (idToken: string) => Promise<void>
  signOut: () => Promise<void>
  loading: boolean
  isAuthenticating: boolean // 로그인 처리 중 플래그 (IdToken 방식 로그인 시 사용)
  setIsAuthenticating: (value: boolean) => void
}

// 인증 이벤트 타입
type AuthEvent = 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | 'USER_UPDATED' | 'INITIAL_SESSION';

// 인증 컨텍스트 생성
const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [pseudoUserId, setPseudoUserId] = useState<string>("")
  const [loading, setLoading] = useState(true)
  const [isAuthenticating, setIsAuthenticating] = useState(false) // 로그인 처리 중 플래그
  const location = useLocation()
  const navigate = useNavigate()
  
  // pseudoUserId 초기화 중복 방지 플래그
  const pseudoUserIdInitializedRef = useRef<boolean>(false)

  // pseudoUserId 설정 (한 번만 실행되도록)
  useEffect(() => {
    const initPseudoUserId = () => {
      // 중복 초기화 방지
      if (pseudoUserIdInitializedRef.current) return
      pseudoUserIdInitializedRef.current = true
      
      // 세션 스토리지에서 pseudoUserId 불러오기
      const savedPseudoUserId = sessionStorage.getItem("pseudoUserId")

      if (savedPseudoUserId) {
        setPseudoUserId(savedPseudoUserId)
      } else {
        // 새로운 pseudoUserId 생성 및 저장
        const newPseudoUserId = uuidv4()
        setPseudoUserId(newPseudoUserId)
        sessionStorage.setItem("pseudoUserId", newPseudoUserId)
      }
    }
    
    initPseudoUserId()
  }, [])

  // 인증 상태 변경 이벤트 핸들러
  const handleAuthStateChange = useCallback((event: AuthEvent, session: any) => {
    if (event === 'SIGNED_IN') {
      if (session?.user) {
        setUser(session.user)
      }
    } else if (event === 'SIGNED_OUT') {
      setUser(null)
    } else if (event === 'USER_UPDATED') {
      // 유저 메타데이터 변경 시에만 새 객체로 교체
      if (session?.user) {
        setUser(session.user)
      }
    } else if (event === 'TOKEN_REFRESHED' || event === 'INITIAL_SESSION') {
      // TOKEN_REFRESHED: JWT만 갱신, 유저 데이터 변경 없음
      // INITIAL_SESSION: 구독 시 자동 발생 — 동일 유저면 객체 참조 유지
      // 새 객체 참조를 만들면 useOrdersByDate 등에서 불필요한 전체 리로드 발생
      if (session?.user) {
        setUser(prev => prev?.id === session.user.id ? prev : session.user)
      }
    }

    setLoading(false)
  }, [])

  // 인증 상태 감지 설정
  useEffect(() => {
    // 현재 세션 확인 (초기화 시)
    const checkInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (session?.user) {
        setUser(session.user)
      }
      setLoading(false)
    }
    
    checkInitialSession()
    
    // 인증 상태 변경 리스너
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => handleAuthStateChange(event as AuthEvent, session)
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [handleAuthStateChange])

  // Google 로그인
  const signInWithGoogle = useCallback(async (idToken: string) => {
    try {
      // 로그인 처리 중 플래그 설정 (App.tsx의 자동 리다이렉트 방지)
      setIsAuthenticating(true)
      
      // 기존 세션 클리어
      await supabase.auth.signOut()
      
      const { error } = await supabase.auth.signInWithIdToken({
        provider: "google",
        token: idToken,
      });
      
      if (error) {
        setIsAuthenticating(false)
        throw error;
      } else {
        // AuthCallback으로 이동 (isAuthenticating은 AuthCallback에서 해제)
        navigate("/auth/callback");
      }
    } catch (err) {
      setIsAuthenticating(false)
      logError('Google 로그인 중 오류:', err)
      throw err; // 에러를 상위로 전파하여 호출자가 처리할 수 있도록 함
    }
  }, [navigate])

  // 카카오 로그인
  const signInWithKakao = useCallback(async (options?: AuthOptions) => {
    try {
      // 기존 세션 클리어
      await supabase.auth.signOut()

      // 클리어한 현재 경로 (빈 문자열이면 기본값 사용)
      const currentPath = location.pathname.replace(/^\/+/, '') || 'home'

      // Kakao OAuth 설정
      const redirectUrl = options?.redirectTo || 
        `${window.location.origin}/auth/callback?from=${currentPath}`
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'kakao',
        options: {
          redirectTo: redirectUrl,
          queryParams: {
            scope: 'account_email profile_image',
            prompt: 'consent',
            // provider_token 반환을 위한 설정
            return_provider_token: 'true'
          }
        }
      })
      
      if (error) {
        throw error
      }
    } catch (err) {
      logError('카카오 로그인 중 오류:', err)
      throw err; // 에러를 상위로 전파하여 호출자가 처리할 수 있도록 함
    }
  }, [location.pathname])

  // 로그아웃
  const signOut = useCallback(async () => {
    try {
      // 1. 로컬 스토리지에서 토큰 + 사용자 범용 키 제거 (먼저 실행)
      const removeTokens = () => {
        // localStorage에서 auth 토큰 + 사용자 scoped 키 직접 제거
        const allKeys = Object.keys(localStorage)
        allKeys.forEach(key => {
          if (key.startsWith('sb-') || key.startsWith('active_template_id_')) {
            localStorage.removeItem(key)
          }
        })
      }
      
      removeTokens()
      
      // 2. Supabase 로그아웃 호출
      const { error } = await supabase.auth.signOut()
      if (error) {
        logError('Supabase 로그아웃 에러:', error.message)
        throw error
      }
      
      // 3. 홈으로 리디렉션
      setTimeout(() => {
        window.location.href = '/'
      }, 100)
    } catch (err) {
      logError('로그아웃 중 오류 발생:', err)
      throw err;
    }
  }, [])

  // 컨텍스트 값
  const contextValue = useMemo(() => ({
    user,
    pseudoUserId,
    signInWithKakao,
    signInWithGoogle,
    signOut,
    loading,
    isAuthenticating,
    setIsAuthenticating
  }), [
    user,
    pseudoUserId,
    signInWithKakao,
    signInWithGoogle,
    signOut,
    loading,
    isAuthenticating
  ])

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

// 인증 컨텍스트 사용 훅
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth는 AuthProvider 내부에서만 사용할 수 있습니다')
  }
  return context
} 